<template>
	<div>
		<div class="bg-white shadow sm:rounded-lg mt-6">
			<div class="px-4 py-5 sm:p-6 mb-4">
				<div class="sm:items-center">
					<div class="sm:flex-auto">
						<span class="flex items-center">
							<h1 class="text-base font-semibold leading-6 text-gray-900">Team Members</h1>
							<p class="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-600 text-gray-200"> {{ users.length + (users.length === 1 ? ' member' : ' members')}} </p></span>
						<p class="my-2 text-sm text-gray-700">
							<span v-if="props.currentUser.current_team.allow_nonadmin_invites || props.currentUser.isAdminOfCurrentTeam">View, invite, and set permissions for team members.

								<br><br>
								<div class="bg-amber-100 rounded-xl p-4">Note that new users are billed automatically once they've joined, using saved payment details as stored in the Billing Portal. Removing users will produce an account credit.</div>
								<br><br>Share this link to invite team members to join your team.
							</span>
							<span v-else>Your admin has disabled the share link for team members. Contact your team admin(s) listed below to add users to your team.</span>
						</p>

					</div>
					<div class="flex" v-if="props.currentUser.current_team.allow_nonadmin_invites || props.currentUser.isAdminOfCurrentTeam">
						<div class="flex-1">
							<input type="link" name="string" id="link" disabled class="shadow-sm p-2 border bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" :value="inviteUrl">
						</div>
						<div class="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
							<button @click="copyToClipboard" :disabled="copySuccessful" data-heap="copy-invite-link" type="button" class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
								{{ copySuccessful ? 'Copied!' : 'Copy invite link' }}
							</button>
						</div>
					</div>
					<div>
						<a @click="resetInviteLink" data-heap="reset-invite-link" v-if="props.currentUser.isAdminOfCurrentTeam" class="mt-2 text-sm text-indigo-700 cursor-pointer">Reset your invite link</a>
					</div>


					<span v-if="props.currentUser.isAdminOfCurrentTeam">
						<hr class="my-6">

						<SwitchGroup as="div" class="">
							<span class="flex"><SwitchLabel as="h3" class="font-medium leading-6 text-gray-900" passive>Allow users to join based on domain?</SwitchLabel><span v-if="userJoinFromDomainSaved" class="ml-2 text-green-600 font-bold">Saved!</span></span>
							<div class="mt-2 sm:flex sm:items-start sm:justify-between">
								<div class="max-w-xl text-xs text-gray-500">
									<SwitchDescription>If you'd like to allow members of your team to automatically join this team based on their email domain, enable this option. Else, all users will need to be manually invited.</SwitchDescription>
								</div>
								<div class="mt-5 sm:mt-0 sm:ml-6 sm:flex sm:flex-shrink-0 sm:items-center">
									<Switch v-model="userJoinFromDomainBoolean" data-heap="join-from-domain-toggle" :class="[userJoinFromDomainBoolean ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2']">
										<span aria-hidden="true" :class="[userJoinFromDomainBoolean ? 'translate-x-5' : 'translate-x-0', 'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
									</Switch>
								</div>
							</div>
						</SwitchGroup>


						<hr class="my-6">

						<SwitchGroup as="div" class="">
							<span class="flex"><SwitchLabel as="h3" class="font-medium leading-6 text-gray-900" passive>Allow non-admins to invite other users?</SwitchLabel><span v-if="allowNonAdminsToInviteSaved" class="ml-2 text-green-600 font-bold">Saved!</span></span>
							<div class="mt-2 sm:flex sm:items-start sm:justify-between">
								<div class="max-w-xl text-xs text-gray-500">
									<SwitchDescription>Your invite link is unique to your team. Check this option to allow non-admins from your team to see and share your link. Note that if your link is published broadly, any user will be able to join regardless of who shared the link.</SwitchDescription>
								</div>
								<div class="mt-5 sm:mt-0 sm:ml-6 sm:flex sm:flex-shrink-0 sm:items-center">
									<Switch v-model="allowNonAdminsToInviteBoolean"  data-heap="nonadmin-invite-toggle" :class="[allowNonAdminsToInviteBoolean ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2']">
										<span aria-hidden="true" :class="[allowNonAdminsToInviteBoolean ? 'translate-x-5' : 'translate-x-0', 'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
									</Switch>
								</div>
							</div>
						</SwitchGroup>
					</span>

				</div>

			</div>
		</div>

		<div class="mt-8 flex flex-col bg-white">
			<div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
				<div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
					<div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
						<table class="min-w-full divide-y divide-gray-300">
							<thead class="bg-gray-50">
								<tr>
									<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
									<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
									<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">GAuth</th>
									<th v-if="currentUser.isAdminOfCurrentTeam" scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
										<span class="sr-only">Change Role</span>
									</th>
									<th v-if="currentUser.isAdminOfCurrentTeam" scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
										<span class="sr-only">Remove</span>
									</th>
								</tr>
							</thead>
							<tbody class="divide-y divide-gray-200 bg-white">
								<tr v-for="user in users" :key="user.id">
									<td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
										<p v-if="user.email === currentUser.email" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-600 text-gray-200">
											YOU
										</p>
										{{ user.email }}
									</td>
									<td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ user.pivot.role }}</td>

									<td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
										<div class="p-1 rounded-full text-xs px-4 w-fit"
											 :class="[user.google_auth_account ? (user.google_auth_account.access_token_expiry ? 'bg-green-200 text-green-500' : 'bg-red-200 text-red-500')  : 'bg-gray-200 text-gray-600']">
											{{ user.google_auth_account ? (user.google_auth_account.access_token_expiry ? 'Valid' : 'Expired') : 'Not Setup'}}
										</div>
									</td>


									<td v-if="user.email !== currentUser.email && currentUser.isAdminOfCurrentTeam" class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
										<button @click="handleUserChange('toggleRole',user.id)" data-heap="toggle-user-role" class="text-indigo-600 hover:text-indigo-900 cursor-pointer">{{ user.pivot.role === 'Admin' ? 'Demote' : 'Promote' }}</button>
									</td>
									<td v-if="user.email !== currentUser.email && currentUser.isAdminOfCurrentTeam" class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
										<button @click="handleUserChange('remove',user.id)"  data-heap="remove-user-from-team" class="text-indigo-600 hover:text-indigo-900 cursor-pointer">Remove</button>
									</td>

								</tr>

							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>


	</div>



</template>

<script setup>

import {Switch, SwitchDescription, SwitchGroup, SwitchLabel} from "@headlessui/vue";
import {computed, onMounted, ref, toRef, toRefs, watch} from "vue";
import { router } from '@inertiajs/vue3'

const props = defineProps({
	users: {type: Object},
	currentUser: {type: Object},
	baseUrl: {type: String}
});

const userJoinFromDomainBoolean = ref(Boolean(props.currentUser.current_team.allow_domain_signups));
const allowNonAdminsToInviteBoolean = ref(Boolean(props.currentUser.current_team.allow_nonadmin_invites));

const userJoinFromDomainSaved = ref(false);
const allowNonAdminsToInviteSaved = ref(false);


watch(
	() => userJoinFromDomainBoolean.value,
	(newValue) => {
		changeSetting();
		userJoinFromDomainSaved.value = true;
		setTimeout(() => userJoinFromDomainSaved.value = false, 4000)
	}
);

watch(
	() => allowNonAdminsToInviteBoolean.value,
	(newValue) => {
		changeSetting();
		allowNonAdminsToInviteSaved.value = true;
		setTimeout(() => allowNonAdminsToInviteSaved.value = false, 4000)
	}
);



const inviteCode = ref(props.currentUser.current_team.invite_code);

const inviteUrl = computed(() => props.baseUrl + '/team/invite/' + inviteCode.value);
const copySuccessful = ref(false);

const copyToClipboard = () => {
	copySuccessful.value = true

	navigator.clipboard.writeText(inviteUrl.value)
		.then(() => {
			console.log('Invite URL copied to clipboard');

			setTimeout(() => copySuccessful.value = false, 4000)
			// You can also show a success message to the user if needed
		})
		.catch((error) => {
			console.error('Error copying to clipboard:', error);
			// Handle the error, e.g., show an error message to the user
		});
};


function resetInviteLink() {
	axios.post('/team-management/resetLink', {
		team: props.currentUser.current_team
	})
		.then((response) => {
			inviteCode.value = response.data;
			console.log(response.data);
		})
		.catch((error) => {
			console.log("error");
		});
}


function changeSetting() {
	// resync

	axios.post('/team-management/update', {
		userJoinFromDomain: userJoinFromDomainBoolean.value,
		allowNonAdminsToInvite: allowNonAdminsToInviteBoolean.value,
		team: props.currentUser.current_team
	})
		.then((response) => {
			console.log(response.data);
		})
		.catch((error) => {
			console.log("error");
		});
}

function handleUserChange(operation,user){
	// alert(operation,user);

	axios.post('team-management/user/update',{
		operation: operation,
		user: user,
		team: props.currentUser.current_team.id
	})
		.then((response) => {
			Inertia.reload();
			console.log(response.data);
		})
		.catch((error) => {
			console.log("error");
			location.reload();
		});
}


</script>
